import { lazy, Suspense, useEffect, useState, type ComponentType, type LazyExoticComponent } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useAppSettings } from '@/hooks/useBetaStatus';
import type { MaintenanceType } from '@/pages/MaintenancePage';

const MaintenancePage = lazy(() => import('@/pages/MaintenancePage'));
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const BetaLandingPage = lazy(() => import('@/pages/BetaLandingPage'));
const ComingSoonPage = lazy(() => import('@/pages/ComingSoonPage'));

const AboutTeaserPage = lazy(() => import('@/pages/teasers/AboutTeaserPage'));
const PricingTeaserPage = lazy(() => import('@/pages/teasers/PricingTeaserPage'));
const FeaturesTeaserPage = lazy(() => import('@/pages/teasers/FeaturesTeaserPage'));

// `?bypass=admin` lets the owner preview the live experience while a maintenance
// flag is enabled. We persist it in sessionStorage so internal navigation keeps
// the bypass without requiring the query param on every link.
const BYPASS_QUERY_PARAM = 'bypass';
const BYPASS_STORAGE_KEY = 'bookiapp:maintenance-bypass';

function readBypassFlag(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const search = new URLSearchParams(window.location.search);
    if (search.get(BYPASS_QUERY_PARAM) === 'admin') {
      sessionStorage.setItem(BYPASS_STORAGE_KEY, '1');
      return true;
    }
    return sessionStorage.getItem(BYPASS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function useMaintenanceBypass() {
  const { user, isLoaded } = useUser();
  const [bypassQuery, setBypassQuery] = useState<boolean>(() => readBypassFlag());

  useEffect(() => {
    setBypassQuery(readBypassFlag());
  }, []);

  // Treat any signed-in admin as a bypass user. publicMetadata is set in Clerk;
  // we also fall back to the URL/sessionStorage flag for unauthenticated previews.
  const isAdmin = isLoaded && !!user && user.publicMetadata?.role === 'admin';
  return isAdmin || bypassQuery;
}

interface GateProps {
  children: React.ReactNode;
}

/**
 * Gate for marketing/blog/public pages.
 *
 * When `marketing_maintenance_mode` is ON, render the maintenance page instead.
 * Admins (or users with `?bypass=admin`) always see the real page so they can
 * verify the deployment before flipping the flag back.
 */
export function MarketingGate({ children }: GateProps) {
  const { data: settings, isLoading } = useAppSettings();
  const isBypass = useMaintenanceBypass();

  // While loading settings, show children (don't flash a maintenance page).
  // Worst case: a user briefly sees the real page, which is acceptable.
  if (isLoading || !settings || isBypass) return <>{children}</>;

  if (settings.marketing_maintenance_mode) {
    return (
      <Suspense fallback={null}>
        <MaintenancePage
          type={settings.app_maintenance_mode ? 'full' : 'marketing'}
          estimatedMinutes={settings.maintenance_estimated_minutes}
        />
      </Suspense>
    );
  }

  return <>{children}</>;
}

/**
 * Gate for app pages — landlord dashboard, sign-in, guest portal.
 *
 * When `app_maintenance_mode` is ON, render the maintenance page instead.
 * Admins always bypass so they can finish the migration that triggered the
 * maintenance window in the first place.
 */
export function AppGate({ children }: GateProps) {
  const { data: settings, isLoading } = useAppSettings();
  const isBypass = useMaintenanceBypass();

  if (isLoading || !settings || isBypass) return <>{children}</>;

  if (settings.app_maintenance_mode) {
    return (
      <Suspense fallback={null}>
        <MaintenancePage
          type={settings.marketing_maintenance_mode ? 'full' : 'app'}
          estimatedMinutes={settings.maintenance_estimated_minutes}
        />
      </Suspense>
    );
  }

  return <>{children}</>;
}

/**
 * Root `/` selector — picks which landing variant to render based on
 * the `active_landing_page` admin setting. Marketing maintenance still wins
 * via MarketingGate above this, so this only controls the "happy path".
 */
export function DynamicLandingRoute() {
  const { data: settings, isLoading } = useAppSettings();

  // Admin preview override — lets the owner force a specific landing variant
  // from the admin settings page without flipping `active_landing_page`.
  const previewOverride = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('coming_soon')
    : null;
  if (previewOverride === 'preview') {
    return (
      <Suspense fallback={null}>
        <ComingSoonPage />
      </Suspense>
    );
  }

  // While we wait, render LandingPage as the safest default — it's what
  // existed before this feature shipped, so behaviour is unchanged on first
  // paint for visitors who don't see the loaded setting yet.
  if (isLoading || !settings) {
    return (
      <Suspense fallback={null}>
        <LandingPage />
      </Suspense>
    );
  }

  switch (settings.active_landing_page) {
    case 'coming_soon':
      return (
        <Suspense fallback={null}>
          <ComingSoonPage />
        </Suspense>
      );
    case 'beta':
      return (
        <Suspense fallback={null}>
          <BetaLandingPage />
        </Suspense>
      );
    case 'maintenance':
      return (
        <Suspense fallback={null}>
          <MaintenancePage
            type="full"
            estimatedMinutes={settings.maintenance_estimated_minutes}
          />
        </Suspense>
      );
    case 'main':
    default:
      return (
        <Suspense fallback={null}>
          <LandingPage />
        </Suspense>
      );
  }
}

/**
 * Marketing teaser gate.
 *
 * On /about, /pricing, /features the visitor sees:
 *   - active_landing_page === 'coming_soon' → the matching teaser page
 *     (short pre-launch copy + email signup), so we don't reveal the full
 *     marketing pitch while the rest of the site is in pre-launch mode.
 *   - any other value → the real marketing page.
 *
 * Admins (and ?bypass=admin previews) always see the real page so the owner
 * can keep editing the production marketing copy without flipping a flag.
 * Signed-in hosts see the real page too: the teaser is for anonymous
 * visitors, while a logged-in host who follows "Odaberi plan" / an invite /
 * a payment e-mail must reach the real pricing and be able to pay.
 *
 * MarketingGate (maintenance scope) still wraps these routes upstream, so an
 * actual maintenance window will preempt the teaser when needed.
 */
type TeaserVariant = 'about' | 'pricing' | 'features';

const TEASERS: Record<TeaserVariant, LazyExoticComponent<ComponentType>> = {
  about: AboutTeaserPage,
  pricing: PricingTeaserPage,
  features: FeaturesTeaserPage,
};

interface TeaserGateProps {
  variant: TeaserVariant;
  children: React.ReactNode;
}

export function TeaserGate({ variant, children }: TeaserGateProps) {
  const { data: settings, isLoading } = useAppSettings();
  const isBypass = useMaintenanceBypass();
  const { isSignedIn } = useAuth();

  // Don't flash a teaser to admins, signed-in hosts or while settings are
  // still loading — showing the full page momentarily is the safer mistake
  // (no email leak), and the bypass flag has to be explicit for previews.
  if (isLoading || !settings || isBypass || isSignedIn) return <>{children}</>;

  if (settings.active_landing_page === 'coming_soon') {
    const Teaser = TEASERS[variant];
    return (
      <Suspense fallback={null}>
        <Teaser />
      </Suspense>
    );
  }

  return <>{children}</>;
}

export type { MaintenanceType };
